import { Close, HelpOutline } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { type IntlShape, useIntl } from 'react-intl';
import { Vector3 } from 'three';
import { EUnitType, EWindingOrder } from '../../@types/Enums';
import { EHistoryAction } from '../../hooks/useUndoRedo';

// --- Imports for Hierarchy Construction ---
import FloorModel from '../../models/standard/FloorModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../../models/standard/PhysicalBoundary';
import RoomModel from '../../models/standard/RoomModel';
import SpaceModel from '../../models/standard/SpaceModel';
import StandardWallModel from '../../models/standard/StandardWallModel';
// ------------------------------------------

import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useDrawing } from '../../provider/DrawingProvider';
import { useModalContext } from '../../provider/ModalProvider';
import { usePlan } from '../../provider/PlanProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { IconRoomForm1, IconRoomForm2, IconRoomForm3, IconRoomForm4, IconRoomShape1, IconRoomShape2, IconRoomShape3, IconRoomShape4 } from '../../assets/icons';
import { fetchAttributesForObject, updateWallGeometryAttributes } from '../../utils/attributes/attributesForObjectLoader';
import { ObjectType } from '../../utils/attributes/defaultObjectsAndAttributes';

import {
  CM_TO_INCH_MULTIPLIER, DEFAULT_WALL_DEPTH, DEFAULT_WALL_LENGTHS,
  DEFAULT_WALL_LENGTH_IMPERIAL,
  MAX_WALL_DEPTH,
  MAX_WALL_DEPTH_IMPERIAL,
  MAX_WALL_LENGTH_ROOM_SHAPE_CREATOR,
  MAX_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL,
  MIN_WALL_DEPTH,
  MIN_WALL_DEPTH_IMPERIAL,
  MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR,
  MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL,
  SampleRoomId,
  SAMPLE_ROOM_1_ID,
  SAMPLE_ROOM_2_ID,
  SAMPLE_ROOM_3_ID,
  SAMPLE_ROOM_4_ID
} from '../../utils/constants';
import { getLocalizedUnitLabel } from '../../utils/conversionUtils';
import { getBoundingBox } from '../../utils/exportUtils';
import {
  canCreateRoomWithTwoNinetyDegreeCorners,
  canFormClosedRoom,
  canFormRegularRoom,
  createComplexRoom,
  createRegularRoom,
  createRoomWithTwoNintyDegressCorners,
  ROOM_SHAPE_STRICTNESS_FACTOR
} from '../../utils/roomGeometryUtils';
import { calculateWindingOrder, reorderWindingOrderToClockwise } from '../../utils/vectorUtils';

const styles = {
  root: {
    width: '100%',
    maxWidth: '900px',
    maxHeight: 'calc(100vh - 48px)',
    overflow: 'auto',
    borderRadius: '20px',
    bgcolor: '#fff',
    color: '#444',
    fontFamily: 'Roboto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '56px',
    px: 2,
    borderBottom: '1px solid #d8d8d8',
  },
  headline: {
    fontFamily: 'Roboto',
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
  },
  headlineMedium: {
    fontFamily: 'Roboto',
    fontSize: '24px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  body1: {
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '0.5px',
  },
  body2: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    letterSpacing: '0.5px',
  },
  content: {
    px: { xs: 3, sm: 5 },
    pt: 2,
    pb: 3,
  },
  shapeSelector: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: { xs: 1.5, sm: 3 },
    alignItems: 'start',
    mb: 3,
  },
  shapeOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 1,
    minWidth: 0,
    border: 0,
    background: 'transparent',
    color: '#444',
    cursor: 'pointer',
    fontFamily: 'Roboto',
    p: 0,
  },
  shapeIconFrame: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: { xs: 72, sm: 112 },
    height: { xs: 72, sm: 112 },
    borderRadius: '14px',
    transition: 'background-color 120ms ease, box-shadow 120ms ease, color 120ms ease',
    '& svg': {
      display: 'block',
    },
  },
  titleInput: {
    mb: 4,
    '& .MuiOutlinedInput-root': {
      height: 54,
      borderRadius: '14px',
      fontSize: '16px',
      fontStyle: 'italic',
      color: '#444',
    },
    '& input': {
      py: 0,
    },
  },
  editorGrid: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
    alignItems: 'center',
    gap: { xs: 3, md: 5 },
  },
  fieldsColumn: {
    maxWidth: { xs: '100%', md: 362 },
  },
  inputRow: {
    mb: 1.2,
  },
  numberInput: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: 36,
      borderRadius: '10px',
      fontFamily: 'Roboto',
      fontSize: '16px',
      pr: 1,
    },
    '& input': {
      py: 0,
      pl: 2,
    },
  },
  createButtonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    mt: 4,
  },
  createButton: {
    height: 54,
    px: 3,
    borderRadius: '18px',
    backgroundColor: '#009fe3',
    boxShadow: '0 3px 6px rgba(0,0,0,0.25)',
    fontFamily: 'Roboto',
    fontSize: '16px',
    fontWeight: 500,
    letterSpacing: '0.5px',
    '&:hover': {
      backgroundColor: '#08aeea',
    },
  },
};

type RoomFieldConfig = {
  field: string;
  label: string;
};

type RoomShapeOption = {
  id: SampleRoomId;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const ROOM_SHAPE_IDS: SampleRoomId[] = [SAMPLE_ROOM_1_ID, SAMPLE_ROOM_2_ID, SAMPLE_ROOM_3_ID, SAMPLE_ROOM_4_ID];

const roomShapeOptions: RoomShapeOption[] = [
  { id: SAMPLE_ROOM_1_ID, Icon: IconRoomForm1 },
  { id: SAMPLE_ROOM_2_ID, Icon: IconRoomForm2 },
  { id: SAMPLE_ROOM_3_ID, Icon: IconRoomForm3 },
  { id: SAMPLE_ROOM_4_ID, Icon: IconRoomForm4 },
];

const roomInputFields: Record<SampleRoomId, RoomFieldConfig[]> = {
  [SAMPLE_ROOM_1_ID]: [
    { field: 'W1', label: 'W1' },
    { field: 'W2', label: 'W2' },
    { field: 'W3', label: 'W3' },
    { field: 'W4', label: 'W4' },
  ],
  [SAMPLE_ROOM_2_ID]: [
    { field: 'W1', label: 'W1' },
    { field: 'W2', label: 'W2' },
    { field: 'W3', label: 'W3' },
    { field: 'W4', label: 'W4' },
  ],
  [SAMPLE_ROOM_3_ID]: [
    { field: 'W1', label: 'W1' },
    { field: 'W2', label: 'W2' },
    { field: 'W3', label: 'W3' },
    { field: 'W4', label: 'W4' },
  ],
  [SAMPLE_ROOM_4_ID]: [
    { field: 'W1', label: 'W1' },
    { field: 'W2', label: 'W2' },
    { field: 'W4', label: 'W4' },
    { field: 'W5', label: 'W5' },
  ],
};

const getRoomShapeLabel = (intl: IntlShape, id: SampleRoomId) =>
  intl.formatMessage({ id: 'SelectRoomSizeModal.roomShapeLabel', defaultMessage: 'Raumform {number}' }, { number: id });

const getDefaultRoomName = (intl: IntlShape, id: SampleRoomId) =>
  intl.formatMessage({ id: 'SelectRoomSizeModal.defaultRoomName', defaultMessage: 'Raum {number}' }, { number: id });

type WallLengthValues = { [key: string]: number };

const getTemplateDefaults = (roomId: SampleRoomId, unitType: EUnitType): WallLengthValues => ({
  ...((unitType === EUnitType.METRIC ? DEFAULT_WALL_LENGTHS[roomId] : DEFAULT_WALL_LENGTH_IMPERIAL[roomId]) || {}),
});

const getDefaultLengths = (roomId: SampleRoomId, unitType: EUnitType): WallLengthValues => {
  const { A = 0, B = 0, C = 0, D = 0, E = 0 } = getTemplateDefaults(roomId, unitType);

  switch (roomId) {
    case SAMPLE_ROOM_2_ID:
      return {
        W1: A,
        W2: B,
        W3: C,
        W4: D,
      };
    case SAMPLE_ROOM_3_ID:
      return {
        W1: A,
        W2: B,
        W3: C,
        W4: D,
      };
    case SAMPLE_ROOM_4_ID:
      return {
        W1: A,
        W2: B,
        W4: D,
        W5: E,
      };
    case SAMPLE_ROOM_1_ID:
    default:
      return {
        W1: A,
        W2: B,
        W3: C,
        W4: D,
      };
  }
};

const getGeometryLengthsFromWallInputs = (roomId: SampleRoomId, wallLengths: WallLengthValues): number[] => {
  const { W1 = 0, W2 = 0, W3 = 0, W4 = 0, W5 = 0 } = wallLengths;

  switch (roomId) {
    case SAMPLE_ROOM_4_ID:
      return [W1, W2, Math.hypot(W4, W5), W4, W5];
    case SAMPLE_ROOM_1_ID:
    case SAMPLE_ROOM_2_ID:
    case SAMPLE_ROOM_3_ID:
    default:
      return [W1, W2, W3, W4];
  }
};

const getDefaultWallThickness = (unitType: EUnitType) =>
  unitType === EUnitType.METRIC ? Math.round(DEFAULT_WALL_DEPTH * 10) : Math.round(DEFAULT_WALL_DEPTH * 10 * CM_TO_INCH_MULTIPLIER);

const toInternalLength = (value: number, unitType: EUnitType) => (unitType === EUnitType.METRIC ? value / 10 : value / 10 / CM_TO_INCH_MULTIPLIER);

const roomShapePreviewIcons: Record<SampleRoomId, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  [SAMPLE_ROOM_1_ID]: IconRoomShape1,
  [SAMPLE_ROOM_2_ID]: IconRoomShape2,
  [SAMPLE_ROOM_3_ID]: IconRoomShape4,
  [SAMPLE_ROOM_4_ID]: IconRoomShape3,
};

const RoomShapePreview = ({ roomId }: { roomId: SampleRoomId }) => {
  const Icon = roomShapePreviewIcons[roomId];

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
      <Icon width="100%" height="100%" style={{ maxWidth: 360, maxHeight: 320, overflow: 'visible' }} aria-hidden="true" />
    </Box>
  );
};

const SelectRoomSizeModal: React.FC = () => {
  const [fieldErrors, setFieldErrors] = useState<Map<string, string>>(new Map());
  const { selectedRoomId, setIsModalShown, setSelectedRoomId } = useModalContext();
  const intl = useIntl();
  const { setRoomCompleted } = useDrawing();
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const { setBuilding, setBuildingWalls, building, updateRoomAttributes, updateFloorAttributes } = useBuilding();
  const { unitType } = useUnitContext();

  const [isLoading, setIsLoading] = useState(false);

  const cmLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'centimeter'), [intl]);
  const inLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, 'inch'), [intl]);
  const minValue = useMemo(() => (unitType === EUnitType.METRIC ? MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR : MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL), [unitType]);
  const maxValue = useMemo(() => (unitType === EUnitType.METRIC ? MAX_WALL_LENGTH_ROOM_SHAPE_CREATOR : MAX_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL), [unitType]);
  const minThicknessValue = useMemo(() => (unitType === EUnitType.METRIC ? MIN_WALL_DEPTH : MIN_WALL_DEPTH_IMPERIAL), [unitType]);
  const maxThicknessValue = useMemo(() => (unitType === EUnitType.METRIC ? MAX_WALL_DEPTH : MAX_WALL_DEPTH_IMPERIAL), [unitType]);
  const unitLabel = unitType === EUnitType.METRIC ? cmLocalizedLabel : inLocalizedLabel;
  const activeRoomId = (ROOM_SHAPE_IDS.includes(selectedRoomId as SampleRoomId) ? selectedRoomId : SAMPLE_ROOM_1_ID) as SampleRoomId;

  const [roomName, setRoomName] = useState<string>(() => getDefaultRoomName(intl, activeRoomId));
  const [wallLengths, setWallLengths] = useState<WallLengthValues>(() => getDefaultLengths(activeRoomId, unitType));
  const [wallThickness, setWallThickness] = useState<number>(() => getDefaultWallThickness(unitType));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!ROOM_SHAPE_IDS.includes(selectedRoomId as SampleRoomId)) setSelectedRoomId(SAMPLE_ROOM_1_ID);
  }, [selectedRoomId, setSelectedRoomId]);

  useEffect(() => {
    setWallLengths(getDefaultLengths(activeRoomId, unitType));
    setRoomName(getDefaultRoomName(intl, activeRoomId));
    setFieldErrors(new Map());
  }, [activeRoomId, intl, unitType]);

  useEffect(() => {
    setWallThickness(getDefaultWallThickness(unitType));
  }, [unitType]);

  const getWallThicknessValidation = () => {
    if (!Number.isFinite(wallThickness) || wallThickness < minThicknessValue || wallThickness > maxThicknessValue) {
      return {
        status: false,
        helperText: intl.formatMessage(
          { id: 'SelectRoomSizeModal.wallThicknessRangeError', defaultMessage: 'Die Wandstärke muss zwischen {min} und {max} {unit} liegen.' },
          { min: minThicknessValue, max: maxThicknessValue, unit: unitLabel },
        ),
      };
    }

    return { status: true };
  };

  const handleAccept = () => {
    setErrorMessage(null);
    setIsLoading(true);

    if (!getWallThicknessValidation().status) {
      setIsLoading(false);
      return;
    }

    setFieldErrors(new Map());
    const newWallLengths = getGeometryLengthsFromWallInputs(activeRoomId, wallLengths);
    const adjustedWallLengths = newWallLengths.map((length) => (unitType === EUnitType.METRIC ? length / 10 : length / 10 / CM_TO_INCH_MULTIPLIER));
    const wallDepth = toInternalLength(wallThickness, unitType);

    // Executed immediately. Scene is NOT cleared!
    _createNewRoom(adjustedWallLengths, wallDepth);
  };

  const _createNewRoom = (newWallLengths: number[], wallDepth: number) => {
    const [A, B, C, D, E] = newWallLengths;
    const tmpFieldErrors: Map<string, string> = new Map();

    const wallPointCalculation: Record<SampleRoomId, () => Vector3[]> = {
      [SAMPLE_ROOM_1_ID]: () => {
        if (canFormRegularRoom(A, B, C, D)) return createRegularRoom(A, B);
        const twoNinety = canCreateRoomWithTwoNinetyDegreeCorners(A, B, C, D);
        if (twoNinety.isPossible && twoNinety.possiblePermutations) return createRoomWithTwoNintyDegressCorners(A, B, C, D);
        if (canFormClosedRoom(A, B, C, D)) return createComplexRoom(A, B, C, D);
        return [];
      },
      [SAMPLE_ROOM_2_ID]: () => {
        const minimumWallLength =
          ((unitType === EUnitType.METRIC ? MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR : MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL) + ROOM_SHAPE_STRICTNESS_FACTOR) * 0.1;
        if (A >= minimumWallLength && C <= A - ROOM_SHAPE_STRICTNESS_FACTOR) {
          return [new Vector3(0, 0, 0), new Vector3(A, 0, 0), new Vector3(A, -B, 0), new Vector3(A - C, -B, 0), new Vector3(A - C, -B - D, 0), new Vector3(0, -B - D, 0)];
        }
        if (C > A - ROOM_SHAPE_STRICTNESS_FACTOR)
          tmpFieldErrors.set(
            'W3',
            intl.formatMessage(
              { id: 'SelectRoomSizeModal.maxWallLengthError', defaultMessage: '{label} muss kleiner oder gleich {max} {unit} sein.' },
              { label: 'W3', max: (A - ROOM_SHAPE_STRICTNESS_FACTOR) * 10, unit: unitLabel },
            ),
          );
        return [];
      },
      [SAMPLE_ROOM_3_ID]: () => [
        new Vector3(0, 0, 0),
        new Vector3(A, 0, 0),
        new Vector3(A, -B, 0),
        new Vector3(A + C, -B, 0),
        new Vector3(A + C, -B - D, 0),
        new Vector3(A, -B - D, 0),
        new Vector3(A, -B - D - B, 0),
        new Vector3(0, -B - D - B, 0),
      ],
      [SAMPLE_ROOM_4_ID]: () => {
        const minimumWallLength =
          ((unitType === EUnitType.METRIC ? MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR : MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL) + ROOM_SHAPE_STRICTNESS_FACTOR) * 0.1;
        if (A >= minimumWallLength && E >= minimumWallLength && D <= A - ROOM_SHAPE_STRICTNESS_FACTOR && B <= E - ROOM_SHAPE_STRICTNESS_FACTOR) {
          return [new Vector3(0, 0, 0), new Vector3(A, 0, 0), new Vector3(A, -B, 0), new Vector3(D, -E, 0), new Vector3(0, -E, 0)];
        }
        if (B > E - ROOM_SHAPE_STRICTNESS_FACTOR)
          tmpFieldErrors.set(
            'W2',
            intl.formatMessage(
              { id: 'SelectRoomSizeModal.maxWallLengthError', defaultMessage: '{label} muss kleiner oder gleich {max} {unit} sein.' },
              { label: 'W2', max: (E - ROOM_SHAPE_STRICTNESS_FACTOR) * 10, unit: unitLabel },
            ),
          );
        if (D > A - ROOM_SHAPE_STRICTNESS_FACTOR)
          tmpFieldErrors.set(
            'W4',
            intl.formatMessage(
              { id: 'SelectRoomSizeModal.maxWallLengthError', defaultMessage: '{label} muss kleiner oder gleich {max} {unit} sein.' },
              { label: 'W4', max: (A - ROOM_SHAPE_STRICTNESS_FACTOR) * 10, unit: unitLabel },
            ),
          );
        return [];
      },
    };

    let roomPoints = wallPointCalculation[activeRoomId]();
    const hasError = roomPoints.length === 0;
    setFieldErrors(tmpFieldErrors);

    if (hasError) {
      setErrorMessage(intl.formatMessage({ id: 'SelectRoomSizeModal.error' }));
      setIsLoading(false);
      return;
    }

    // =========================================================================
    // CENTER-LINE MITERING CALCULATION
    // =========================================================================
    // The previous generation created perfect INNER lines. Now we offset them
    // outwards by half the wall depth and calculate the exact mathematical intersections
    // so the Center-Line geometry maintains the user's requested inner dimensions!
    const h = wallDepth / 2;
    const nPts = roomPoints.length;

    const offsetLines = roomPoints.map((p, i) => {
      const nextP = roomPoints[(i + 1) % nPts];
      const dir = new Vector3().subVectors(nextP, p).normalize();
      
      // Calculate outward normal (Assuming Clockwise polygon creation above)
      const normal = new Vector3(-dir.y, dir.x, 0); 
      const offsetP = p.clone().add(normal.multiplyScalar(h));
      
      return { p: offsetP, dir };
    });

    const intersectLines2D = (p1: Vector3, dir1: Vector3, p2: Vector3, dir2: Vector3): Vector3 | null => {
      const crossProd = dir1.x * dir2.y - dir1.y * dir2.x;
      if (Math.abs(crossProd) < 1e-6) return null; // Parallel, should not happen here
      const t = ((p2.x - p1.x) * dir2.y - (p2.y - p1.y) * dir2.x) / crossProd;
      return new Vector3(p1.x + t * dir1.x, p1.y + t * dir1.y, 0);
    };

    const centerLinePoints: Vector3[] = [];
    for (let i = 0; i < nPts; i++) {
      const prev = offsetLines[(i - 1 + nPts) % nPts];
      const curr = offsetLines[i];
      const intersection = intersectLines2D(prev.p, prev.dir, curr.p, curr.dir);
      
      if (intersection) {
        centerLinePoints.push(intersection);
      } else {
        centerLinePoints.push(curr.p.clone()); // Fallback
      }
    }

    // Anchor the new center-line polygon back to (0,0) locally so smart grid placement works
    const originShift = centerLinePoints[0].clone().negate();
    roomPoints = centerLinePoints.map((p) => p.add(originShift));
    // =========================================================================

    // --- SMART GRID PLACEMENT LOGIC ---
    let newMinX = Infinity;
    let newMaxX = -Infinity;
    let newMinY = Infinity;
    let newMaxY = -Infinity;

    for (const p of roomPoints) {
      newMinX = Math.min(newMinX, p.x);
      newMaxX = Math.max(newMaxX, p.x);
      newMinY = Math.min(newMinY, p.y);
      newMaxY = Math.max(newMaxY, p.y);
    }

    const WORLD_UNITS_PER_METER = 10;
    const RIGHT_LIMIT = 90 * WORLD_UNITS_PER_METER;
    const ROOM_SPACING = 10;

    let shiftX = 0;
    let shiftY = 0;

    if (building && building.walls && building.walls.length > 0) {
      const existingBBox = getBoundingBox(building);
      const newRoomWidth = newMaxX - newMinX;
      
      const potentialRightX = existingBBox.maxX + (ROOM_SPACING * 3);
      const wouldExceedRight = potentialRightX + newRoomWidth > RIGHT_LIMIT;

      if (wouldExceedRight) {
        shiftX = existingBBox.minX - newMinX;
        shiftY = existingBBox.minY - (ROOM_SPACING * 3) - newMaxY;
      } else {
        shiftX = potentialRightX - newMinX;
        shiftY = existingBBox.maxY - newMaxY; 
      }
    } else {
      shiftX = -(newMinX + newMaxX) / 2;
      shiftY = -(newMinY + newMaxY) / 2;
    }

    roomPoints = roomPoints.map((p) => new Vector3(p.x + shiftX, p.y + shiftY, p.z));
    // ----------------------------------------

    // 1. Create Initial Walls
    const createWalls = (points: Vector3[]) => points.map((point, index, arr) => new StandardWallModel(point, arr[(index + 1) % arr.length], wallDepth));
    let orderedWalls = createWalls(roomPoints);

    // 2. Ensure Winding Order
    if (calculateWindingOrder(orderedWalls as any) === EWindingOrder.COUNTERCLOCKWISE) {
      orderedWalls = reorderWindingOrderToClockwise(orderedWalls as any) as StandardWallModel[];
    }

    // 3. Construct Hierarchy (Floor -> Space -> Room)
    const newFloor = new FloorModel(undefined, null, [], undefined); 

    const boundary = new PhysicalBoundaryModel(orderedWalls);
    const edges = (orderedWalls || []).filter(Boolean).map((w) => new BoundaryEdge(w, 0, 1, 'A', false));
    boundary.setEdges(edges);

    const newSpace = new SpaceModel(newFloor, [], [], [], [boundary], orderedWalls);
    const newRoom = new RoomModel(roomName || '', [newSpace], []);

    // 4. APPEND to Global State instead of overwriting
    setBuilding((prev) => ({
      ...prev,
      rooms: [...(prev.rooms || []), newRoom],
      walls: [...(prev.walls || []), ...orderedWalls], 
      isSetByUndoRedo: false,
      lastAction: EHistoryAction.WALLS_CHANGED,
    }));

    // 5. Handle Attributes (Async)
    orderedWalls.forEach((wall) => {
      fetchAttributesForObject(wall as any, ObjectType.Wall, regionCode).then((attrs) => {
        const updatedAttrs = updateWallGeometryAttributes(wall, unitType, attrs);
        setBuildingWalls(
          (walls) =>
            walls.map((w) => {
              if (w.uuid !== wall.uuid || !(w instanceof StandardWallModel)) return w;
              const updatedWall = new StandardWallModel(w.startPoint, w.endPoint, w.depth, w.windows, w.doors, [], updatedAttrs, w.uuid, w.passages);
              updatedWall.sides.A.radiators = [...w.sides.A.radiators];
              updatedWall.sides.B.radiators = [...w.sides.B.radiators];
              return updatedWall;
            }),
          EHistoryAction.WALL_STANDARD_ATTRIBUTE_UPDATED,
        );
      });
    });

    Promise.all([fetchAttributesForObject(newRoom as any, ObjectType.Room, regionCode), fetchAttributesForObject(newFloor as any, ObjectType.Floor, regionCode)])
      .then(([roomAttrs, floorAttrs]) => {
        if (Array.isArray(roomAttrs)) updateRoomAttributes(roomAttrs as any);
        if (Array.isArray(floorAttrs)) updateFloorAttributes(newFloor.uuid, floorAttrs as any);

        setIsModalShown(false);
        setRoomCompleted(true);
        setIsLoading(false);
      })
      .catch((e) => {
        console.error('Error fetching attributes', e);
        setIsLoading(false);
      });
  };

  const handleChange = (inputValue: string, field: string) => {
    const value = Number(inputValue);
    setWallLengths((prev) => ({ ...prev, [field]: value }));
    setFieldErrors(new Map());
  };

  const getDisplayFieldLabel = (field: string) => roomInputFields[activeRoomId].find((config) => config.field === field)?.label ?? field;

  const handleCheckInputFieldCondition = (value: string, additionalParameters: Array<string>) => {
    const parsedValue = Number(value);
    const field = additionalParameters[0];
    const displayLabel = getDisplayFieldLabel(field);
    const isSpecialCase = ((activeRoomId === SAMPLE_ROOM_2_ID || activeRoomId === SAMPLE_ROOM_4_ID) && field === 'W1') || (activeRoomId === SAMPLE_ROOM_4_ID && field === 'W5');
    if (isSpecialCase) {
      const minimumWallLength = (unitType === EUnitType.METRIC ? MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR : MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL) + ROOM_SHAPE_STRICTNESS_FACTOR * 10;
      if (parsedValue < minimumWallLength) {
        return {
          status: false,
          helperText: intl.formatMessage(
            { id: 'SelectRoomSizeModal.wallLengthRangeError', defaultMessage: '{label} muss zwischen {min} und {max} {unit} liegen.' },
            { label: displayLabel, min: minimumWallLength, max: maxValue, unit: unitLabel },
          ),
        };
      }
    }
    if ((!isSpecialCase && parsedValue < minValue) || parsedValue > maxValue) {
      return {
        status: false,
        helperText: intl.formatMessage(
          { id: 'SelectRoomSizeModal.wallLengthRangeError', defaultMessage: '{label} muss zwischen {min} und {max} {unit} liegen.' },
          { label: displayLabel, min: minValue, max: maxValue, unit: unitLabel },
        ),
      };
    }
    if (fieldErrors.has(field)) return { status: false, helperText: fieldErrors.get(field) };
    return { status: true };
  };

  const getNumberInputs = () =>
    roomInputFields[activeRoomId].map(({ field, label }) => {
      const validation = handleCheckInputFieldCondition(String(wallLengths[field] ?? ''), [field]);

      return (
        <Box key={`numberInput-${field}`} sx={styles.inputRow}>
          <Typography component="label" sx={{ ...styles.body2, display: 'block', color: validation.status ? '#555' : '#d32f2f', mb: 0.25, pl: 2 }}>
            {label}
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={wallLengths[field] ?? ''}
            error={!validation.status}
            helperText={validation.helperText}
            onChange={(event) => handleChange(event.target.value, field)}
            variant="outlined"
            sx={styles.numberInput}
            inputProps={{ step: 1, min: minValue, max: maxValue }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{unitLabel}</InputAdornment>,
            }}
          />
        </Box>
      );
    });

  const wallThicknessValidation = getWallThicknessValidation();

  return (
    <Box component="div" id="selectRoomSizeBox" sx={styles.root}>
        <Box sx={styles.header}>
          <Typography sx={styles.headline}>{intl.formatMessage({ id: 'SelectRoomSizeModal.title', defaultMessage: 'Raum definieren' })}</Typography>
          <IconButton
            aria-label={intl.formatMessage({ id: 'SelectRoomSizeModal.close', defaultMessage: 'Raumdefinition schließen' })}
            onClick={() => setIsModalShown(false)}
            sx={{ color: '#444' }}
          >
            <Close />
          </IconButton>
        </Box>

        <Box sx={styles.content}>
          <Box sx={styles.shapeSelector} aria-label={intl.formatMessage({ id: 'PredefinedRoomsModal.chooseRoomShape', defaultMessage: 'Raumform wählen' })}>
            {roomShapeOptions.map(({ id, Icon }) => {
              const isSelected = activeRoomId === id;

              return (
                <Box
                  key={id}
                  component="button"
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => {
                    setSelectedRoomId(id);
                    setErrorMessage(null);
                    setFieldErrors(new Map());
                  }}
                  sx={styles.shapeOption}
                >
                  <Box
                    sx={{
                      ...styles.shapeIconFrame,
                      bgcolor: isSelected ? '#efefef' : 'transparent',
                      color: isSelected ? '#444' : '#9d9d9d',
                      boxShadow: isSelected ? 'inset 0 0 0 2px #dedede, 0 1px 4px rgba(0,0,0,0.18)' : 'none',
                    }}
                  >
                    <Icon width="70%" height="70%" />
                  </Box>
                  <Typography sx={{ ...(isSelected ? styles.headlineMedium : styles.headline), fontSize: { xs: '18px', sm: '24px' }, whiteSpace: 'nowrap' }}>
                    {getRoomShapeLabel(intl, id)}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <TextField
            id="roomName"
            placeholder={getDefaultRoomName(intl, activeRoomId)}
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            fullWidth
            sx={styles.titleInput}
          />

          <Box sx={styles.editorGrid}>
            <Box sx={styles.fieldsColumn}>
              <Typography sx={{ ...styles.body1, mb: 2 }}>{intl.formatMessage({ id: 'SelectRoomSizeModal.wallLengthSectionTitle', defaultMessage: 'Länge der Wände:' })}</Typography>
              {errorMessage && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errorMessage}
                </Alert>
              )}
              {getNumberInputs()}

              <Box sx={styles.inputRow}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, pl: 2 }}>
                  <Typography component="label" sx={{ ...styles.body2, color: '#555' }}>
                    {intl.formatMessage({ id: 'SelectRoomSizeModal.wallThicknessLabel', defaultMessage: 'Wandstärke in {unit}' }, { unit: unitLabel })}
                  </Typography>
                  <Tooltip
                    title={intl.formatMessage({
                      id: 'SelectRoomSizeModal.wallThicknessTooltip',
                      defaultMessage: 'Du legst hier zwar die Wandstärke für alle Raumwände fest, kannst sie später jedoch für jede einzelne Wand individuell anpassen.',
                    })}
                  >
                    <HelpOutline sx={{ width: 17, height: 17, color: '#9d9d9d' }} />
                  </Tooltip>
                </Box>
                <TextField
                  fullWidth
                  type="number"
                  value={wallThickness}
                  error={!wallThicknessValidation.status}
                  helperText={wallThicknessValidation.helperText}
                  onChange={(event) => {
                    setWallThickness(Number(event.target.value));
                    setErrorMessage(null);
                  }}
                  variant="outlined"
                  sx={{ ...styles.numberInput, maxWidth: 330 }}
                  inputProps={{ step: 1, min: minThicknessValue, max: maxThicknessValue }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{unitLabel}</InputAdornment>,
                  }}
                />
              </Box>
            </Box>

            <RoomShapePreview roomId={activeRoomId} />
          </Box>

          <Box sx={styles.createButtonWrapper}>
            <Button variant="contained" onClick={handleAccept} sx={styles.createButton} disabled={isLoading}>
              {isLoading ? <CircularProgress color="inherit" size={24} /> : intl.formatMessage({ id: 'SelectRoomSizeModal.createRoom', defaultMessage: 'RAUM ERSTELLEN' })}
            </Button>
          </Box>
        </Box>
    </Box>
  );
};

export default SelectRoomSizeModal;
